/* @flow */

import * as React from 'react';
import Select, {
    mergeStyles,
    components,
    type SelectBaseProps,
    type ControlProps,
    type SingleValueProps,
    type OptionProps,
} from 'react-select';

import Async from 'react-select/async';
import AsyncCreatable from 'react-select/async-creatable';
import Creatable from 'react-select/creatable';

import {colors} from '../colors';

import {getCustomMenuList} from './custom-menu-list';
import {getNoOptionsMessage} from './no-options-message';
import type {ActionMeta} from './select-picker-generic-creatable';
import {selectPickerGenericStyles} from './select-picker-generic-styles';

export type NoOptionsMessageProps = {|
    icon?: React.Node,
    text: React.Node | string,
    cta?: React.Node,
|};

export type FooterButtonProps = {
    label: string,
    href?: string,
    onClick?: () => void,
    icon?: React.ComponentType<*>,
};

export type Props = {|
    // We really want to use ElementConfig, but it wasn't added until flow 0.63,
    // so instead we made a custom lib-def with default props made optional
    ...$Exact<SelectBaseProps>,
    /** Fades the option to green when true */
    blinkSuccess?: boolean,
    /** Not allowing multiple selections in this component */
    isMulti?: false,
    /** Provide an `onClear` to make this clearable */
    isClearable: false,
    valueKey: string,
    labelKey: string,
    /** Simplified / abstracted version of react-select's onChange method.  This only gets called with an object. */
    onChange: (newValue: Object, action: ActionMeta) => void,
    /** Added for simplicity, components usually need to handle clearing differently from changes */
    onClear?: () => void,
    hasError?: ?boolean,
    getRef?: React.Ref<*>,
    /** This signifies an Async, multi-select */
    loadOptions?: (query: string) => Promise<Object[]>,
    isDark?: boolean,
    controlIcon?: React.ComponentType<*>,
    hideDividerWhenEmpty?: boolean,
    minHeight?: number,
    customNoOptionsMessage?: NoOptionsMessageProps,
    footerButton?: FooterButtonProps,
    /** Optional right-aligned content */
    rightContent?: {
        // Function to render content on the right side of each option
        getOptionContent?: (option: Object) => React.Node,
        // Function to render content on the right side of selected value
        getSelectedValueContent?: (option: Object) => React.Node,
    },
    noBorder?: boolean,
    resetAfterSelect?: boolean,
    showSelectedValueTitle?: boolean,
    menuWidth?: number,
    // This component is not intended to be creatable, if you need this
    // functionality, look to other components first.
    isCreatable?: boolean,
    onCreateOption?: (inputValue: string) => void,
    isValidNewOption?: (inputValue: string) => boolean,
    formatCreateLabel?: (inputValue: string) => React.Node,
    createOnBlur?: boolean,
    menuZIndex?: number,
|};

/**
 * Thin wrapper around react-select.  Takes all the standard react-select props.
 *
 * Only for single value, non-creatable pickers
 */
export class SelectPickerGeneric extends React.Component<Props> {
    static defaultProps = {
        valueKey: 'value',
        labelKey: 'label',
        isClearable: false,
    };

    render() {
        const {
            valueKey,
            labelKey,
            rightContent,
            // eslint-disable-next-line no-unused-vars
            onChange,
            styles,
            getRef,
            loadOptions,
            customNoOptionsMessage,
            footerButton,
            onCreateOption,
            isValidNewOption,
            formatCreateLabel,
            ...restProps
        } = this.props;
        const componentStyles = {
            control: (base, state) => {
                let controlStyles = {
                    ...base,
                    backgroundColor: this.props.isDark ? colors.offWhiteDk : colors.white,
                    border: getBorder(this.props),
                    minHeight: this.props.minHeight, // undefined will be default
                };

                if (state.isDisabled || this.props.isDisabled) {
                    controlStyles = {
                        ...controlStyles,
                        color: 'var(--grey-lt)',
                        backgroundColor: 'var(--off-white)',
                        opacity: 0.6,
                    };
                }

                return controlStyles;
            },
            placeholder: (base, state) => ({
                ...base,
                color: this.props.blinkSuccess && !state.isDisabled ? colors.green : base.color,
                transition: 'color 200ms linear',
            }),
            menu: (base) => {
                let menuStyles = {
                    ...base,
                    zIndex: this.props.menuZIndex ?? '5',
                    wordBreak: 'break-word',
                };

                if (this.props.menuWidth) {
                    menuStyles = {
                        ...menuStyles,
                        width: `${this.props.menuWidth}px`,
                    };
                }

                return menuStyles;
            },
            // We set the padding bottom to 42 to account for the height of the footer (which is 42px)
            ...(footerButton && {
                menuList: (base) => ({
                    ...base,
                    paddingBottom: 42,
                }),
            }),
            singleValue: (base, state) => ({
                ...base,
                color: this.props.blinkSuccess && !state.isDisabled ? colors.green : colors.grey,
                transition: 'color 200ms linear',
                ...(rightContent &&
                    rightContent.getSelectedValueContent && {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        maxWidth: 'calc(100% - 10px)',
                    }),
            }),
            indicatorSeparator: (base, state) => {
                let display;

                if (
                    !this.props.onClear ||
                    (this.props.onClear &&
                        state.selectProps.hideDividerWhenEmpty &&
                        !state.selectProps.value)
                ) {
                    display = 'none';
                }

                return {
                    ...base,
                    display,
                };
            },
            dropdownIndicator: (base, state) => {
                return {
                    ...base,
                    display: state.isDisabled ? 'none' : 'flex',
                };
            },
        };
        let mergedStyles = mergeStyles(selectPickerGenericStyles, componentStyles);
        // Add in styles from props
        if (styles) {
            mergedStyles = mergeStyles(mergedStyles, styles);
        }

        let Component;
        if (this.props.isCreatable) {
            Component = loadOptions ? AsyncCreatable : Creatable;
        } else {
            Component = loadOptions ? Async : Select;
        }

        return (
            <Component
                {...restProps}
                ref={getRef}
                loadOptions={loadOptions}
                defaultOptions={true}
                cacheOptions={null}
                styles={mergedStyles}
                isMulti={false}
                isClearable={Boolean(this.props.onClear)}
                onChange={this.handleChange}
                getOptionLabel={(option) => option[labelKey]}
                getOptionValue={(option) => option[valueKey]}
                placeholder={this.props.placeholder}
                onCreateOption={onCreateOption}
                isValidNewOption={isValidNewOption}
                formatCreateLabel={formatCreateLabel}
                components={{
                    Control,
                    Option: getOptionLabel(rightContent),
                    SingleValue: getSingleValue(rightContent),
                    NoOptionsMessage: getNoOptionsMessage(customNoOptionsMessage),
                    MenuList: getCustomMenuList(footerButton),
                    ...(this.props.components ? this.props.components : {}),
                }}
                onBlur={this.handleBlur}
            />
        );
    }

    handleChange: $PropertyType<$PropertyType<Select, 'props'>, 'onChange'> = (
        newValue,
        actionMeta
    ) => {
        // Should not happen because we are not using multi select, but just to be safe:
        if (Array.isArray(newValue)) {
            // There's a bug, where backspacing to clear sends an array.
            // https://github.com/JedWatson/react-select/pull/3216 should fix it
            if (newValue.length === 0) {
                if (this.props.onClear) {
                    this.props.onClear();
                }

                return;
            }
            throw new Error(
                `Somehow got an array value in an onChange (${actionMeta.action}): ${JSON.stringify(
                    newValue
                )}`
            );
        }

        if (actionMeta.action === 'clear' || newValue === null || typeof newValue === 'undefined') {
            if (this.props.onClear) {
                this.props.onClear();
            }
        } else {
            this.props.onChange(newValue, actionMeta);
        }
    };

    handleBlur = (e: FocusEvent) => {
        if (this.props.createOnBlur && this.props.onCreateOption && this.props.isValidNewOption) {
            const target = e.target;
            if (target && 'value' in target) {
                // $FlowIgnore Thanks for nothing, Flow
                if (this.props.isValidNewOption(target.value)) {
                    // $FlowIgnore
                    this.props.onCreateOption(target.value);
                }
            }
        }
    };
}

const Control = ({children, ...props}: {...ControlProps}) => (
    <components.Control {...props}>
        {props.selectProps.controlIcon ? (
            <div
                style={{
                    fill: colors.grey,
                    color: colors.grey,
                    marginLeft: 12,
                    display: 'flex',
                }}
            >
                <props.selectProps.controlIcon color={colors.grey} size={15} wrapWithDiv={false} />
            </div>
        ) : undefined}
        {children}
    </components.Control>
);

const getOptionLabel =
    (rightContent) =>
    ({data, ...props}: {...OptionProps}) => {
        if (!rightContent || !rightContent.getOptionContent) {
            return <components.Option {...props} data={data} />;
        }

        return (
            <components.Option {...props} data={data}>
                <div className='flex justify-sb align-center full-width'>
                    <span>{data.label}</span>
                    <span style={{color: colors.grey}}>
                        {rightContent && rightContent.getOptionContent
                            ? rightContent.getOptionContent(data)
                            : ''}
                    </span>
                </div>
            </components.Option>
        );
    };

const getSingleValue =
    (rightContent?: {getSelectedValueContent?: (option: Object) => React.Node}) =>
    ({data: option, ...props}: {...SingleValueProps}) => {
        if (!rightContent || !rightContent.getSelectedValueContent) {
            if (props.selectProps.showSelectedValueTitle) {
                const content = props.selectProps.formatOptionLabel
                    ? props.selectProps.formatOptionLabel(option)
                    : option.label;

                return (
                    <components.SingleValue {...props} data={option}>
                        <span
                            title={
                                props.selectProps.showSelectedValueTitle ? option.label : undefined
                            }
                        >
                            {content}
                        </span>
                    </components.SingleValue>
                );
            }

            return <components.SingleValue {...props} data={option} />;
        }

        if (props.selectProps.showSelectedValueTitle) {
            const content = props.selectProps.formatOptionLabel
                ? props.selectProps.formatOptionLabel(option)
                : option.label;

            return (
                <components.SingleValue data={option} {...props}>
                    <span
                        title={props.selectProps.showSelectedValueTitle ? option.label : undefined}
                    >
                        {content}
                    </span>
                    <span style={{color: colors.grey}}>
                        {rightContent.getSelectedValueContent(option)}
                    </span>
                </components.SingleValue>
            );
        }

        return (
            <components.SingleValue data={option} {...props}>
                <span>{option.label}</span>
                <span style={{color: colors.grey}}>
                    {rightContent.getSelectedValueContent(option)}
                </span>
            </components.SingleValue>
        );
    };

const getBorder = (props: Props) => {
    if (props.noBorder) {
        return '0';
    }

    return props.hasError ? `1px solid ${colors.rose}` : `1px solid ${colors.offWhiteDk}`;
};
